import { getCode, sqlQuery } from '@/shared/helpers/api';
import { encodeIdBase64 } from '@/shared/helpers/utils';
import { http } from '@/shared/httpWrapper';
import { isAuthenticationFailure } from '@/shared/services/authService.ts';
import { useDisabledFeaturesStore } from '@/stores/disabledFeaturesStore.ts';

import { RouteLocationNormalizedGeneric, RouteLocationNormalizedLoadedGeneric, RouteLocationRaw } from 'vue-router';

export type Roles = {
    IsAuthenticatedUser: string;
    IsAdministrator: string;
    CanEditRelease: string;
    CanEditLibrary: string;
};

export const roles = {
    IsAuthenticatedUser: 'IsAuthenticatedUser',
    IsAdministrator: 'IsAdministrator',
    CanEditRelease: 'CanEditRelease',
    CanEditLibrary: 'CanEditLibrary'
};

export async function ensureLoggedIn(_to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): Promise<boolean> {
    if (window.authService.isSignedIn()) {
        return true;
    } else {
        await window.authService.signIn();
        return true;
    }
}

const signInFailedRoute = 'SignInFailed';

/**
 * Loads the permissions before any other guard needs them. When no access token can be acquired, the user is
 * sent to a page that says so and offers to sign in again, instead of a misleading "Access Denied" or a blank page.
 */
export async function ensurePermissionsLoaded(to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): Promise<boolean | RouteLocationRaw> {
    if (to.name === signInFailedRoute) {
        return true;
    }

    try {
        await window.authService.getPermissions();
        return true;
    } catch (error) {
        if (isAuthenticationFailure(error)) {
            return { name: signInFailedRoute, query: { page: to.fullPath } };
        }
        throw error;
    }
}

// Only pages that explain an access problem are reachable without a Common Library role, to avoid
// redirect loops. Other pages are gated, so that missing access is reported before less pressing
// application-internal issues such as a disabled feature.
const routesWithoutCommonLibraryAccess = ['NoAccess', 'Unauthorized', signInFailedRoute];

function readRequiredPermissions(permissions: unknown): string[] {
    if (Array.isArray(permissions) && permissions.every((permission): permission is string => typeof permission === 'string')) {
        return permissions;
    }

    return [roles.IsAdministrator];
}

/**
 * Restricts the whole application for users without any Common Library role, sending them to a page
 * explaining how to request access.
 *
 * The check is done in the frontend because the backend `CanRead` policy is currently an alias for
 * `IsAuthenticatedUser`, and is therefore granted to every authenticated user. When the backend starts
 * enforcing a real `CanRead` requirement, this guard should check that permission instead.
 */
export function requireCommonLibraryAccess(to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): boolean | RouteLocationRaw {
    if (routesWithoutCommonLibraryAccess.includes(to.name as string)) {
        return true;
    } else if (window.authService.hasAnyCommonLibraryRole()) {
        return true;
    } else {
        return { name: 'NoAccess' };
    }
}

export async function requireAuthForRoutes(to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): Promise<boolean | RouteLocationRaw> {
    if (to.meta.requireAuth === false) {
        return true;
    } else if (!to.meta.requireAuth) {
        const requiredPermissions = readRequiredPermissions(to.meta.permissions);
        const hasAccess = await window.authService.hasAnyPermission(requiredPermissions);

        if (hasAccess) {
            return true;
        } else {
            return { name: 'Unauthorized', params: { page: to.path } };
        }
    } else {
        return { name: 'Unauthorized', params: { page: to.path } };
    }
}

// Every navigation sets a title, including the ones that supply no meta.title of their own.
// Leaving the previous page's title in place would otherwise strand it on the next page, and since
// unknown urls now resolve to the not found route, 'Not Found' is the title that would be stranded.
export function rewriteTitle(to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): boolean {
    const defaultTitle = 'Common Library';
    const defaultTitleExtension = ` - ${defaultTitle}`;
    if (to.meta?.title instanceof Function) {
        document.title = to.meta.title(to) + defaultTitleExtension;
    } else if (to.meta?.title) {
        document.title = to.meta.title + defaultTitleExtension;
    } else {
        document.title = defaultTitle;
    }
    return true;
}

const oldHashRoutingPaths = [
    '',
    '/ChangeLog',
    '/Library',
    '/Release',
    '/FacilityAndProject',
    '/CableEdit',
    '/Schema',
    '/UoM',
    '/EleComponentType',
    '/Config',
    '/Help',
    '/Report/Query'
];

const alternatives = oldHashRoutingPaths
    .map(s => s.replaceAll('/', '\\/'))
    .join('|');

const hashPattern = /\/?#/;
const pathPattern = new RegExp(`^(?:${alternatives})${hashPattern.source}`, 'i');

export function rewriteLegacyPath(path: string): string | null {
    if (!pathPattern.test(path))
        return null;

    const rewritten = path.replace(
        /(\/)?#(\/)?(\w*)/,
        (_fullMatch, leadingSlash, trailingSlash, path) => {
            if (path) {
                return '/' + path;
            } else if (leadingSlash || trailingSlash) {
                return '/';
            } else {
                return '';
            }
        }
    );

    // '/#//example.com' would otherwise rewrite to '//example.com', which is a protocol relative url
    // pointing at another site. The router treats it as a path today, but collapsing the slashes
    // keeps it safe to render as a link. Backslashes count too: the url spec treats '\' as '/' in
    // this position for http(s), so '/\example.com' resolves to another origin just like '//' does.
    return rewritten.replace(/^[/\\]{2,}/, '/');
}

export function rewriteLegacyRouteUrls(to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): boolean | RouteLocationRaw {
    const destination = rewriteLegacyPath(to.fullPath);
    if (destination !== null) {
        return destination;
    } else {
        return true;
    }
}

/**
 * For all routes hitting the route names 'codeOld' we redirect to the correct code.
 * Redirecting in the 'routes' const is not supported with async calls, using a beforeEnter or beforeEach,
 * is the correct way to redirect if needing to do async operations.
 *
 * https://v3.router.vuejs.org/guide/essentials/redirect-and-alias.html#redirect
 * https://github.com/vuejs/vue-router/issues/2729
 *
 */
export async function redirectOldCode(to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): Promise<boolean | RouteLocationRaw> {
    if (to.name !== 'codeOld') {
        return true;
    } else {
        const codeIdentity = to.params.identity;
        const codeSetName = to.params.codeSetName;

        if (typeof codeIdentity !== 'string' || typeof codeSetName !== 'string') {
            return '/';
        }

        try {
            const code = await getCode(http, codeSetName, codeIdentity);
            if (code.id) {
                const encodedId = encodeIdBase64('Code', code.id);
                const destination = { name: 'code', params: { id: encodedId } };
                return destination;
            } else {
                return '/';
            }
        } catch {
            return '/';
        }
    }
}

export async function redirectProjectMasterByGuid(to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): Promise<boolean | RouteLocationRaw> {
    const guid = to.params.guid as string;

    try {
        const response = await sqlQuery(
            http,
            {
                query: 'SELECT Id FROM ProjectMaster WHERE GUID = @guid',
                arguments: { guid }
            }
        );
        const results = response.data;
        if (results && results.length > 0 && results[0].Id) {
            const encodedId = encodeIdBase64('Code', results[0].Id as number);
            return { name: 'FacilityAndProjectById', params: { id: encodedId } };
        } else {
            return '/FacilityAndProject/';
        }
    } catch {
        return '/FacilityAndProject/';
    }
}

export async function requireFeatureEnabled(to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric): Promise<boolean | RouteLocationRaw> {
    if (to.name === 'DisabledFeature') {
        return true;
    } else {
        const store = useDisabledFeaturesStore();
        const disabledInfo = await store.isRouteDisabled(to.name);
        if (disabledInfo) {
            return {
                name: 'DisabledFeature',
                query: { name: disabledInfo.name }
            };
        } else {
            return true;
        }
    }
}
